// In-world displays. In VR the DOM HUD does not exist, so the card and the caddie's line have to be
// objects: two canvas boards on posts by the tee. On a screen they are off by default (the same
// numbers twice in one frame is noise) — xr.ts turns them on when a session starts.

import * as THREE from 'three';
import { sampleHeight, type Hole } from '@shared/course';
import type { RoomPublicState } from '@shared/protocol';

export interface Board extends THREE.Group {
  userData: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; texture: THREE.CanvasTexture; w: number; h: number };
}

export function makeBoard(w = 1.9, h = 1.1): Board {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = Math.round((1024 * h) / w);
  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 8;
  const face = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: texture, transparent: true }));
  const post = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.06, 1.4, 8),
    new THREE.MeshStandardMaterial({ color: 0x4a3f31, roughness: 1 }),
  );
  post.position.y = -h / 2 - 0.7;
  post.castShadow = true;
  const g = new THREE.Group() as Board;
  g.name = 'board';
  g.add(face, post);
  g.userData = { canvas, ctx: canvas.getContext('2d')!, texture, w, h };
  return g;
}

/** Stand the two boards either side of the tee, back far enough to stay out of the shot. */
export function placeBoards(hole: Hole, score: Board, caddie: Board) {
  const y = sampleHeight(hole, hole.tee.x, hole.tee.z);
  score.position.set(hole.tee.x - 7.6, y + 1.62, hole.tee.z - 4.2);
  caddie.position.set(hole.tee.x + 7.6, y + 1.45, hole.tee.z - 3.6);
}

/** Billboard a board about its post, so it is always legible but never lying on its side. */
export function faceCamera(board: Board, camera: THREE.Camera) {
  board.lookAt(camera.position.x, board.position.y, camera.position.z);
}

export function drawScoreboard(board: Board, state: RoomPublicState | null, accent = '#e0b13c') {
  const { ctx, canvas, texture } = board.userData;
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(16,20,14,0.88)';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 10);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  const brief = state?.spec.holes[state.hole];
  ctx.font = '600 44px ui-sans-serif, system-ui, sans-serif';
  ctx.fillStyle = '#f2efe6';
  ctx.fillText(brief?.name ?? 'Links', 34, 34);
  ctx.font = '400 30px ui-monospace, monospace';
  ctx.fillStyle = 'rgba(242,239,230,.6)';
  ctx.fillText(`hole ${(state?.hole ?? 0) + 1} / ${state?.spec.holes.length ?? 3}${brief ? ` · par ${brief.par}` : ''}`, 34, 92);
  (state?.players ?? []).forEach((p, i) => {
    const y = 150 + i * 52;
    ctx.font = '500 34px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle = p.id === state?.turn ? accent : '#f2efe6';
    ctx.fillText(p.name, 34, y);
    ctx.font = '600 34px ui-monospace, monospace';
    ctx.textAlign = 'right';
    ctx.fillText(String(p.strokes.reduce((a, b) => a + b, 0)), W - 34, y);
    ctx.textAlign = 'left';
  });
  texture.needsUpdate = true;
}

export function drawCaddieBoard(board: Board, text: string, who = 'Dex', source: 'ai' | 'placeholder' = 'placeholder', accent = '#e0b13c') {
  const { ctx, canvas, texture } = board.userData;
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(16,20,14,0.88)';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, 10, H);
  ctx.fillStyle = '#f2efe6';
  ctx.font = '400 38px ui-sans-serif, system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  let line = '', y = 40;
  for (const word of String(text ?? '').split(/\s+/)) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > W - 80) { ctx.fillText(line, 40, y); line = word; y += 48; }
    else line = test;
  }
  if (line) ctx.fillText(line, 40, y);
  ctx.font = '400 26px ui-monospace, monospace';
  ctx.fillStyle = 'rgba(242,239,230,.55)';
  ctx.fillText(`${who}${source === 'placeholder' ? ' · placeholder, no AI key' : ' · ai'}`, 40, H - 50);
  texture.needsUpdate = true;
}
